<template>
    <div>
        <div class="app-title">
            <h2>{{ pageTitle }}</h2>
        </div>
        <section class="app-content">
            <div class="card my-1">
                <div class="card-body">
                    <b-form>
                        <b-alert :show="!!errorMessage" variant="danger" class="py-2">
                            {{ errorMessage }}
                        </b-alert>

                        <!--
                            退回后「修改」模式：资产集合在发起那一刻就定死了（S7 已知约束——
                            这些资产已经是「处置中」，重提时后端忽略 assetIds），
                            所以这里整块置灰、只展示已锁定的明细快照。
                        -->
                        <b-alert :show="isModify" variant="info" class="py-2">
                            {{ $t('label.em.asset.emArchFixedAssetDisposal.assetLockedTip') }}
                        </b-alert>

                        <h6 class="text-muted border-bottom pb-1">
                            {{ $t('label.em.asset.emArchFixedAssetDisposal.assetBlock') }}
                        </h6>

                        <b-form-row v-if="!isModify">
                            <b-col md="6">
                                <b-form-group :label="requiredLabel('assetIds')">
                                    <b-input-group>
                                        <b-form-input v-model="keyword" size="sm" trim
                                                      :placeholder="$t('label.em.asset.emArchFixedAssetDisposal.assetSearchPlaceholder')"
                                                      @keyup.enter.native="searchAssets"></b-form-input>
                                        <b-input-group-append>
                                            <b-button size="sm" variant="outline-primary"
                                                      :disabled="searching" @click="searchAssets">
                                                <i class="fa fa-fw"
                                                   :class="searching ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </b-form-group>
                            </b-col>
                            <b-col md="6" class="d-flex align-items-center">
                                <span class="text-muted">
                                    {{ $t('label.em.asset.emArchFixedAssetDisposal.selectedCount') }}
                                    ：{{ selectedIds.length }}
                                </span>
                            </b-col>
                        </b-form-row>

                        <!--
                            ⚠ MiniTable 的勾选状态（checkedIds / selected）活在组件实例上，
                            重新赋值 :items 只触发 initData()，不会清空勾选。每次换数据都必须
                            assetTableKey++ 强制重建，否则上一次搜索的勾选会残留、把旧 id
                            带进批量发起（memory mmf-minitable-checked-state）。
                        -->
                        <div class="fad-table mb-2">
                            <mini-table :key="assetTableKey"
                                        ref="assetTable"
                                        header-title=""
                                        disable-header
                                        read-only
                                        :enable-row-check="!isModify"
                                        :row-click-check="!isModify"
                                        row-key="id"
                                        :fields="assetFields"
                                        :items="assetRows"
                                        :loading="searching"
                                        @rowChecked="onAssetChecked">
                            </mini-table>
                        </div>

                        <h6 class="text-muted border-bottom pb-1">
                            {{ $t('label.em.asset.emArchFixedAssetDisposal.disposalBlock') }}
                        </h6>
                        <b-form-row>
                            <b-col md="6">
                                <b-form-group :label="requiredLabel('disposalReason')">
                                    <b-form-textarea v-model="form.disposalReason" size="sm"
                                                     rows="3"></b-form-textarea>
                                </b-form-group>
                            </b-col>
                            <b-col md="6">
                                <b-form-group :label="plainLabel('remark')">
                                    <b-form-textarea v-model="form.remark" size="sm" rows="3"></b-form-textarea>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <!--
                            URS 需求_new R67：附件必填，可上传多个文件、无格式限制。
                            attachmentIds 只在进入「修改」模式拉到原单时喂一次，之后靠 idsChanged
                            单向写回 form.attachments，避免「回写 form → 回灌 prop → 组件重新拉列表」
                            的来回抖动（S3 已知约束）。
                        -->
                        <h6 class="text-muted border-bottom pb-1">
                            {{ requiredLabel('attachments') }}
                        </h6>
                        <em-attachment :key="attachmentKey"
                                       class="w-100 mb-2"
                                       get-items-by-self
                                       is-editing
                                       :avoid-null="false"
                                       :attachment-ids="attachmentIds"
                                       @idsChanged="whenAttachmentIdsChanged"></em-attachment>

                        <div class="text-right">
                            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="goBack">
                                <i class="fa fa-fw fa-times"></i>
                                <span class="ml-1">{{ $t('label.em.asset.emArchFixedAssetDisposal.cancel') }}</span>
                            </b-button>
                            <b-button size="sm" variant="primary" :disabled="saving" @click="save">
                                <i class="fa fa-fw" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                                <span class="ml-1">{{ $t('label.em.asset.emArchFixedAssetDisposal.save') }}</span>
                            </b-button>
                        </div>
                    </b-form>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import MiniTable from "@/components/common/mini-table/MiniTable.vue";
import EmAttachment from "@/components/em/EmAttachment";
import emCommonApiMixin from "@/mixin/em/common/api";
import {costCenterText} from "@/views/em/asset/costCenterText";

/**
 * 固定资产处置 | 发起页（验收 #10；URS 需求_new R51–R67）。
 * <p>
 * 两步式发起（spec D12）：本页只 POST /emArchFixedAssetDisposal/save，
 * 保存成功后父类 afterSaveTrigger 写明细快照 + 把资产置「处置中」+ 发 MQ 启动流程，
 * 申请人的「待办」里出现 task_disposal_init，在那里点「提交」才真正进入审批链。
 * <p>
 * 退回后的「修改重提」复用本页：路由带 ?id= 时先按 id 拉回原单再编辑；
 * 此时资产集合已锁定（后端忽略 assetIds），只能改处置原因 / 备注 / 附件（S7 已知约束）。
 */
export default {
    name: "emArchFixedAssetDisposalEditView",
    components: {MiniTable, EmAttachment},
    mixins: [emCommonApiMixin],
    data() {
        return {
            keyword: '',
            searching: false,
            saving: false,
            errorMessage: '',
            // 搜索结果（发起模式）
            assets: [],
            // 已锁定的明细快照（修改模式）
            lockedItems: [],
            // 明细只有 costCenterId / deptId，靠这份下拉补名称
            costCenters: [],
            selectedIds: [],
            // 是否财务人员（GET /emArchFixedAsset/permission）；探测回来之前按「不是」处理，宁可少显示
            finance: false,
            // 换数据必须 ++ 强制重建 MiniTable，否则旧勾选会残留
            assetTableKey: 0,
            attachmentIds: '',
            attachmentKey: 0,
            form: this.emptyForm(),
        }
    },
    computed: {
        baseApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAssetDisposal';
        },
        isModify() {
            return !!this.form.id;
        },
        pageTitle() {
            return this.isModify
                    ? this.$t('title.em.asset.emArchFixedAssetDisposal.edit')
                    : this.$t('title.em.asset.emArchFixedAssetDisposal.create');
        },
        /**
         * 发起模式看搜索结果，修改模式看已锁定的明细快照。
         * 两者字段名一致（OptionVo 与 DisposalItem 的快照字段同名），可以共用一套列定义；
         * 差别只在明细表只存了 costCenterId / deptId，没有名称快照，所以要用
         * GET /emArchFixedAsset/costCenters（含所属部门）在前端补名字。
         */
        assetRows() {
            if (!this.isModify) {
                return this.assets.map(item => Object.assign({}, item, {
                    costCenterText: costCenterText(item.costCenterCode, item.costCenterName)
                }));
            }
            return this.lockedItems.map(item => {
                let cc = this.costCenterMap[item.costCenterId];
                return Object.assign({}, item, {
                    costCenterText: cc ? costCenterText(cc.code, cc.name) : '',
                    deptName: cc ? cc.deptName : ''
                });
            });
        },
        costCenterMap() {
            let map = {};
            for (let item of this.costCenters) {
                map[item.id] = item;
            }
            return map;
        },
        /**
         * 原值 / 已提折旧 / 账面价值三个金额列能不能显示（测试反馈「处置 1」）：
         *   发起模式（候选资产表格）—— 只有财务人员能看；后端 searchAssets 对非财务同样置 null，
         *                              这里隐藏整列只是不让非财务看到一排空白列；
         *   修改模式（退回后重提）    —— 资产早已选定并提交，表格展示的是明细快照而不是候选，
         *                              按「选定后才显示」照常显示，与后续审批页「处置资产明细」一致；
         *                              数值由 GET /detail/{id} 按「财务 ∨ 本单参与人」返回，申请人就是参与人。
         */
        showAmountColumns() {
            return this.isModify || this.finance;
        },
        /**
         * URS R51–R64：固资号 / 子固资号 / 资产名称 / 设备编码 / 成本中心 / 部门 /
         * 使用位置名称 / 原值 / 已提折旧 / 账面价值 / 资本化日期 / 折旧年限 / 折旧年限-月 / 已使用年限。
         * 三个金额列的可见性见 showAmountColumns；折旧年限 / 折旧年限-月两列始终在，值由后端按权限给。
         */
        assetFields() {
            let prefix = 'label.em.asset.emArchFixedAssetDisposal.entity.';
            let amountKeys = ['originalValue', 'accumulatedDepreciation', 'netBookValue'];
            let fields = [
                {key: 'assetNo', label: prefix + 'assetNo'},
                {key: 'subAssetNo', label: prefix + 'subAssetNo'},
                {key: 'assetName', label: prefix + 'assetName'},
                {key: 'equipmentCode', label: prefix + 'equipmentCode'},
                {key: 'costCenterText', label: prefix + 'costCenterName'},
                {key: 'deptName', label: prefix + 'deptName'},
                {key: 'locationName', label: prefix + 'locationName'},
                {key: 'originalValue', label: prefix + 'originalValue'},
                {key: 'accumulatedDepreciation', label: prefix + 'accumulatedDepreciation'},
                {key: 'netBookValue', label: prefix + 'netBookValue'},
                {key: 'capitalizationDate', label: prefix + 'capitalizationDate'},
                {key: 'depreciationYears', label: prefix + 'depreciationYears'},
                {key: 'depreciationMonths', label: prefix + 'depreciationMonths'},
                {key: 'usedYears', label: prefix + 'usedYears'},
            ];
            return this.showAmountColumns ? fields : fields.filter(f => amountKeys.indexOf(f.key) < 0);
        },
    },
    watch: {
        /**
         * 列定义变了 MiniTable 不一定重绘表头，强制重建一次；重建会丢掉组件内的勾选，
         * selectedIds 跟着清空，免得把看不见的旧勾选带进发起（memory mmf-minitable-checked-state）
         */
        showAmountColumns() {
            this.selectedIds = [];
            this.assetTableKey++;
        },
    },
    mounted() {
        if (this.$route.query.id) {
            this.loadCostCenters();
            this.loadExisting(this.$route.query.id);
        } else {
            // 先探测权限再搜：列定义在数据到达之前就定下来，避免用户勾选后表格因列变化被重建
            this.loadPermission().finally(() => this.searchAssets());
        }
    },
    methods: {
        /**
         * 财务人员判定照档案模块：GET /emArchFixedAsset/permission → {finance}（后端 finance_staff 角色）
         */
        loadPermission() {
            return this.$_emApiMixin_get(process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/permission')
                    .then(res => this.finance = !!(res && res.finance))
                    .catch(() => this.finance = false);
        },

        emptyForm() {
            return {
                id: null,
                revision: null,
                assetIds: [],
                disposalReason: null,
                remark: null,
                attachments: null,
            };
        },

        /**
         * 搜索可发起处置的资产：后端只返回「正常」且无在途变更 / 处置的资产（验收 #14），
         * 并带出成本中心 / 部门与财务快照（含已使用年限预览值），这里不需要再做状态过滤。
         */
        searchAssets() {
            if (this.isModify) {
                return;
            }
            this.searching = true;
            // api/get 拿到的就是 body（EmResponseUtils.success 裸透传）
            this.$_emApiMixin_get(`${this.baseApi}/assets/search?keyword=${encodeURIComponent(this.keyword || '')}`)
                    .then(res => this.applyAssets(res || []))
                    .catch(() => this.applyAssets([]))
                    .finally(() => this.searching = false);
        },

        applyAssets(rows) {
            this.assets = Array.isArray(rows) ? rows : [];
            this.selectedIds = [];
            this.assetTableKey++;
        },

        onAssetChecked(ids) {
            let alive = new Set(this.assets.map(o => o.id));
            this.selectedIds = (ids || []).filter(id => alive.has(id));
        },

        /**
         * 退回后修改重提：按 id 取回原单（通用工作流查询，api/post 要 .data 一层），
         * 再拉一次 detail 拿到已锁定的资产明细快照。
         */
        loadExisting(id) {
            this.$_emApiMixin_post(`${this.baseApi}/workflow/find-by-query-form`, {
                idFilter: id,
                workflowQueryTypeFilter: 'todo'
            }).then(response => {
                let list = response.data && response.data.list;
                if (!list || list.length === 0) {
                    return;
                }
                let row = list[0];
                this.form = {
                    id: row.id,
                    revision: row.revision,
                    assetIds: [],
                    disposalReason: row.disposalReason,
                    remark: row.remark,
                    attachments: row.attachments,
                };
                // 后端 default-property-inclusion = NON_NULL，null 字段是整个 key 消失
                this.attachmentIds = row.attachments || '';
                this.attachmentKey++;
                this.loadDetail(id);
            });
        },

        loadCostCenters() {
            this.$_emApiMixin_get(process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/costCenters')
                    .then(res => {
                        this.costCenters = res || [];
                        this.assetTableKey++;
                    });
        },

        loadDetail(id) {
            this.$_emApiMixin_get(`${this.baseApi}/detail/${id}`).then(res => {
                this.lockedItems = (res && res.items) || [];
                this.assetTableKey++;
            });
        },

        /**
         * em-attachment 上传 / 删除后回写附件 id 串（逗号分隔），随保存一起提交
         */
        whenAttachmentIdsChanged(ids) {
            this.form.attachments = ids || null;
        },

        save() {
            let missing = this.firstMissingRequired();
            if (missing) {
                this.errorMessage = `${this.$t('label.em.asset.emArchFixedAssetDisposal.requiredTip')}：${this.$t('label.em.asset.emArchFixedAssetDisposal.entity.' + missing)}`;
                return;
            }
            this.errorMessage = '';
            this.saving = true;
            let payload = Object.assign({}, this.form);
            // 修改模式后端忽略 assetIds，显式不传，避免让人误以为可以换资产
            payload.assetIds = this.isModify ? null : this.selectedIds.slice();
            // assetIds 走 POST body，不拼 query string（memory mmf-batch-request-header-limit：
            // 请求行约 8KB 上限，网关放行但下游 Tomcat 400，且前端表现为静默失败）
            this.$_emApiMixin_post(`${this.baseApi}/save`, payload)
                    .then(() => {
                        this.$bvToast.toast(this.$t('label.em.asset.emArchFixedAssetDisposal.saveSuccess'), {
                            variant: 'success',
                            noCloseButton: true,
                            autoHideDelay: 3000
                        });
                        this.goTodo();
                    })
                    .finally(() => this.saving = false);
        },

        /**
         * 必填：资产（仅发起模式）/ 处置原因 / 附件（URS R51 / R65 / R67）。
         * 快照与已使用年限由后端计算，不在前端校验。
         */
        firstMissingRequired() {
            if (!this.isModify && this.selectedIds.length === 0) {
                return 'assetIds';
            }
            if (!this.form.disposalReason) {
                return 'disposalReason';
            }
            if (!this.form.attachments) {
                return 'attachments';
            }
            return null;
        },

        goTodo() {
            this.$router.push({
                path: '/em/asset/emArchFixedAssetDisposalList',
                query: {pathType: 'todo'}
            });
        },

        goBack() {
            this.$router.go(-1);
        },

        requiredLabel(key) {
            return `${this.$t('label.em.asset.emArchFixedAssetDisposal.entity.' + key)} *`;
        },

        plainLabel(key) {
            return this.$t('label.em.asset.emArchFixedAssetDisposal.entity.' + key);
        },
    }
};
</script>

<style scoped>
/*
   b-table 的 sticky-header 包裹层同时带着 MiniTable 的 .table-cover，而后者的
   `max-height: 100% !important` 盖掉了 bootstrap-vue 自带的 300px 上限；父级高度不是
   确定值时这个百分比解析成 none，表格就按内容整高铺开，把下面的内容盖住。
   所以直接给这一层一个确定上限：表格在自己的 sticky 容器里滚动、表头保持吸顶，
   行数少时依然按内容收缩。外层不要再加 max-height —— 那只会裁剪内容或多出一层滚动条。
*/
.fad-table >>> .b-table-sticky-header {
    max-height: 24rem !important;
}
</style>
